<app-config class="hidden" />
<p-confirmDialog></p-confirmDialog>

<app-bulk-action-dialog
  [visible]="viewDocxExportDialogVisible"
  [action]="viewDocxExportAction"
  [selectedCount]="1"
  [showPageBreakBetweenNotesOption]="false"
  (cancel)="onViewDocxExportDialogCancel()"
  (submit)="onViewDocxExportDialogSubmit($event)">
</app-bulk-action-dialog>

@if (note && !isLoading()) {
  <!-- eagerTabs: the note panel always exists, so printing (File → Print) shows the note on any tab. -->
  <app-note-shell
    [tabs]="noteTabs"
    [(activeTab)]="activeNoteTab"
    [eagerTabs]="['note']"
    [focusMode]="focusMode">

    <!-- ===================== HEADER ===================== -->
    <div noteHeader class="print:hidden">
      <p-tieredmenu #headerMenu [model]="menuItems" [popup]="true" appendTo="body" [autoDisplay]="false" />

      <app-note-header [title]="displayTitle" [derived]="isDisplayTitleDerived" backLabel="Back to notes" (back)="pageMenu('Home')">
        <button
          type="button"
          class="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary "
          [attr.aria-pressed]="isFavorite"
          [attr.aria-label]="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
          [title]="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
          (click)="toggleFavorite()">
          <!-- Favorite: filled red heart; otherwise a hollow black one. -->
        <span
          class="material-symbols-outlined"
          [ngClass]="isFavorite ? 'is-filled text-red-600 dark:text-red-500' : 'text-black dark:text-(--p-text-color)'"
          data-testid="favorite-icon"
          aria-hidden="true">favorite</span>
        </button>

        <button
          type="button"
          class="hidden h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-40 lg:inline-flex"
          aria-label="Ask Notes"
          [title]="!isOnline ? 'AI features are only available in online mode' : 'Ask Notes'"
          [disabled]="!isOnline"
          (click)="openAskNotes()">
          <i class="pi pi-sparkles" aria-hidden="true"></i>
        </button>

        <button
          type="button"
          class="hidden h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:inline-flex"
          aria-label="Export PDF"
          title="Export PDF"
          (click)="pageMenu('DownloadPDF')">
          <span class="material-symbols-outlined" aria-hidden="true">picture_as_pdf</span>
        </button>

        <button
          type="button"
          class="hidden h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:inline-flex"
          aria-label="Print"
          title="Print"
          (click)="pageMenu('Print')">
          <span class="material-symbols-outlined" aria-hidden="true">print</span>
        </button>

        <!-- Icon-only on the narrowest screens so the title keeps room; labelled from sm up. -->
        <p-button
          class="sm:hidden"
          icon="pi pi-pencil"
          size="small"
          ariaLabel="Edit note"
          title="Edit note"
          styleClass="!h-11 !w-11"
          (onClick)="pageMenu('Edit')">
        </p-button>
        <p-button
          class="hidden sm:inline-flex"
          label="Edit"
          icon="pi pi-pencil"
          size="small"
          styleClass="!h-11 !px-3"
          (onClick)="pageMenu('Edit')">
        </p-button>

        <button
          type="button"
          class="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary "
          aria-label="More actions"
          title="More actions"
          aria-haspopup="menu"
          (click)="headerMenu.toggle($event)">
          <span class="material-symbols-outlined" aria-hidden="true">more_vert</span>
        </button>
      </app-note-header>
    </div>

    <!-- ===================== NOTE ===================== -->
    <ng-template notePanel="note">
      <!-- min-height fills the scroller's own visible height (note header + tab strip, ~5.75rem,
           plus the top safe-area inset; there's no footer to account for here, unlike Edit) so the
           editor below still fills the screen even when the note has little content — see the
           editorSurfaceClasses wrapper + [fillHeight] a few lines down. Focus Mode hides the header
           and tab strip entirely, so it only needs the full viewport height. -->
      <div
        id="printable-content"
        class="flex w-full flex-col px-2 py-2 sm:px-3 sm:py-3 lg:px-4"
        [ngClass]="focusMode ? 'min-h-dvh' : 'min-h-[calc(100dvh-5.75rem-env(safe-area-inset-top))]'"
        (pointerdown)="onNoteContentPointerDown($event)"
        (click)="onNoteContentTap($event)">
        <!-- Print only: the note's title (and a short status line) at the top of the printed document.
             The on-screen title lives in the header, which isn't printed. -->
        <header class="note-print-header hidden print:block">
          <h1>{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</h1>
          <p>
            {{ spaceName }}@if (note.Status) { · {{ note.Status }} }@if (note.NoteType) { · {{ note.NoteType }} }@if (note.last_modified) { · Modified {{ note.last_modified | date: 'dd.MM.yyyy HH:mm' }} }
          </p>
        </header>

        @if (errorMessage) {
          <div role="alert" class="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
            {{ errorMessage }}
          </div>
        }

        <!-- One wrapping row: status / type / modified, then colored tags, then the reading tools pushed right.
             Hidden in mobile Focus Mode (see onNoteContentTap/enterFocusMode) so the note content fills the
             viewport; the row's height is captured just before it's removed so the reading position stays put. -->
        @if (!focusMode) {
          <div #focusToolsRow class="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 print:hidden">
            <div class="order-1 flex flex-wrap items-center gap-x-1.5 text-xs text-(--p-text-muted-color) sm:gap-x-2 sm:text-sm">
              <span>{{ spaceName }}</span>
              @if (note.Status) { <span aria-hidden="true">·</span><span class="font-medium text-(--p-text-color)">{{ note.Status }}</span> }
              @if (noteActionRequired) {
                <span class="inline-flex items-center text-orange-600 dark:text-orange-400" role="img" aria-label="Action required" title="Action required"><i class="pi pi-flag-fill text-xs" aria-hidden="true"></i></span>
              }
              @if (notePriorityIndicator.icon) {
                <span class="inline-flex items-center" [ngClass]="notePriorityIndicator.colorClass" role="img" [attr.aria-label]="notePriorityIndicator.label" [title]="notePriorityIndicator.label"><i class="text-xs" [ngClass]="notePriorityIndicator.icon" aria-hidden="true"></i></span>
              }
              @if (note.NoteType) { <span aria-hidden="true">·</span><span>{{ note.NoteType }}</span> }
              @if (note.last_modified) { <span aria-hidden="true">·</span><span><span class="sm:hidden">Mod.</span><span class="hidden sm:inline">Modified</span> {{ note.last_modified | date: 'dd.MMM.yyyy' }}</span> }
              @if (note.Confidential) {
                <span aria-hidden="true">·</span>
                <span class="inline-flex items-center gap-1"><i class="pi pi-lock text-xs" aria-hidden="true"></i>Confidential</span>
              }
            </div>

            @if (tagCount) {
              <div class="order-3 flex flex-wrap items-center gap-1.5 sm:order-2" role="list" aria-label="Tags">
                @for (tag of noteTags; track tag._id ?? tag.name) {
                  <button
                    type="button"
                    role="listitem"
                    class="note-tag-chip inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2.5 text-xs font-medium text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-slate-600 dark:bg-slate-800"
                    [title]="'Show notes tagged ' + tag.name"
                    (click)="filterByTag(tag)">
                    <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'" [title]="tag.color || 'No tag color'"></span>
                    {{ tag.name }}
                  </button>
                }
                <!-- The user's own private tags: shown after Space tags, with a subtle lock. Not a Home filter
                     shortcut — Home filtering by private tags isn't part of this phase. -->
                @for (tag of privateNoteTags; track tag._id) {
                  <span
                    role="listitem"
                    class="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2.5 text-xs font-medium text-(--p-text-color) dark:border-slate-600 dark:bg-slate-800"
                    [title]="'Private tag — only you can see it'">
                    <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
                    <i class="pi pi-lock !text-[0.625rem] text-(--p-text-muted-color)" aria-hidden="true"></i>
                    <span class="sr-only">Private tag:</span>{{ tag.name }}
                  </span>
                }
              </div>
            }

            <!-- Reading tools share the row above (no rows of their own). Zoom expands inline, next to its button. -->
            <div class="order-2 ml-auto flex items-center gap-1 print:hidden sm:order-3">
              @if (showZoomPanel) {
                <div #zoomPanel class="flex items-center gap-2 pr-1" (click)="$event.stopPropagation()">
                  <p-slider
                    class="w-28 sm:w-44"
                    [(ngModel)]="editorZoomFactor"
                    [min]="50"
                    [max]="400"
                    [step]="1"
                    ariaLabel="Editor zoom"
                    (onChange)="onZoomFactorChange('editor', editorZoomFactor)"
                    (onSlideEnd)="persistEditorZoom()">
                  </p-slider>
                </div>
              }
              @if (!showRawMarkup) {
                <button
                  type="button"
                  class="inline-flex h-9 cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 text-sm text-(--p-text-muted-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-primary"
                  [attr.aria-label]="areAllCollapsibleExpanded ? 'Collapse all sections' : 'Expand all sections'"
                  [title]="areAllCollapsibleExpanded ? 'Collapse all sections' : 'Expand all sections'"
                  (click)="toggleAllCollapsibleSections()">
                  <span class="material-symbols-outlined !text-xl" aria-hidden="true">{{ areAllCollapsibleExpanded ? 'unfold_less' : 'unfold_more' }}</span>
                  <span class="hidden lg:inline">{{ areAllCollapsibleExpanded ? 'Collapse' : 'Expand' }}</span>
                </button>
              }
              <button
                #trigger
                type="button"
                class="inline-flex h-9 cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 text-sm tabular-nums text-(--p-text-muted-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-primary"
                aria-label="Zoom"
                title="Zoom"
                [attr.aria-expanded]="showZoomPanel"
                (click)="toggleZoomPanel($event)">
                <span class="material-symbols-outlined !text-xl" aria-hidden="true">zoom_in</span>
                <span class="hidden sm:inline">{{ editorZoomFactor }}%</span>
              </button>
            </div>
          </div>
        }

        <!-- Focus Mode hides the header (and with it the title): show the title above the content
             instead, inside the scroller so it scrolls away with the text. -->
        @if (focusMode) {
          <h1 #focusTitle class="print:hidden mb-3 mt-[max(0.25rem,env(safe-area-inset-top))] break-words px-1 text-2xl font-semibold leading-tight text-(--p-text-color)">
            {{ note.Title | noteDisplayTitle: note.DescriptionHtml }}
          </h1>
        }

        <div class="flex min-h-0 flex-1 flex-col" [ngClass]="editorSurfaceClasses">
        @if (!showRawMarkup) {
          <app-rich-editor
            #descriptionEditor
            class="min-h-0 flex-1"
            editorId="EditDescription"
            [noteId]="note._id"
            [spaceId]="note.spaceId"
            [content]="renderMarkup(note.DescriptionHtml)"
            [readOnly]="true"
            [placeholderText]="''"
            [fillHeight]="true">
          </app-rich-editor>
        } @else {
          <pre class="m-0 min-h-0 flex-1 overflow-x-auto whitespace-pre-wrap break-words text-sm">{{ note.DescriptionHtml || '' }}</pre>
        }
        </div>

        @if (!hasDescription) {
          <app-note-empty-state class="print:hidden" icon="notes" title="This note has no content" description="Use Edit to add some." />
        }
      </div>

      <!-- Focus Mode exit overlay: small, unobtrusive, auto-hides itself (see showFocusExitOverlayFor). -->
      @if (focusMode && showFocusExitOverlay) {
        <div class="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center pt-[max(0.5rem,env(safe-area-inset-top))]" aria-live="polite">
          <button
            type="button"
            class="pointer-events-auto inline-flex h-11 cursor-pointer items-center gap-1.5 rounded-full border-0 bg-surface-900/85 px-4 text-sm font-medium text-white shadow-lg backdrop-blur-sm dark:bg-surface-0/85 dark:text-surface-900"
            (click)="exitFocusModeFromButton($event)">
            <span class="material-symbols-outlined !text-lg" aria-hidden="true">close_fullscreen</span>
            Exit Focus Mode
          </button>
        </div>
      }
    </ng-template>

    <!-- ===================== PROPERTIES ===================== -->
    <ng-template notePanel="properties">
      <div class="grid w-full grid-cols-1 items-start gap-3 px-2 py-2 sm:px-3 sm:py-3 lg:px-4 xl:grid-cols-2">

        <details open class="group rounded-lg border border-surface-200 bg-surface-0 dark:border-surface-700 dark:bg-surface-900">
          <summary class="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-base font-semibold focus-visible:outline-2 focus-visible:outline-primary">
            General
            <span class="material-symbols-outlined transition-transform group-open:rotate-180" aria-hidden="true">expand_more</span>
          </summary>
          <dl class="m-0 grid grid-cols-1 gap-x-6 gap-y-3 px-3 pb-3 sm:grid-cols-2">
            <div>
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Status</dt>
              <dd class="m-0 mt-0.5 text-sm">
                {{ note.Status || '—' }}
                @if (legacyStatusHint; as hint) {
                  <span class="text-xs text-(--p-text-muted-color)">(old status · now {{ hint }})</span>
                }
              </dd>
            </div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Type</dt><dd class="m-0 mt-0.5 text-sm">{{ note.NoteType || '—' }}</dd></div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Confidential</dt><dd class="m-0 mt-0.5 text-sm">{{ note.Confidential ? 'Yes' : 'No' }}</dd></div>
            <div>
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Action required</dt>
              <dd class="m-0 mt-0.5 inline-flex items-center gap-1.5 text-sm" data-testid="view-action-required">
                @if (noteActionRequired) {
                  <i class="pi pi-flag-fill text-xs text-orange-600 dark:text-orange-400" aria-hidden="true"></i>Yes
                } @else {
                  No
                }
              </dd>
            </div>
            <div>
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Priority</dt>
              <dd class="m-0 mt-0.5 inline-flex items-center gap-1.5 text-sm" data-testid="view-priority">
                @if (notePriorityIndicator.icon) {
                  <i class="text-xs" [ngClass]="[notePriorityIndicator.icon, notePriorityIndicator.colorClass]" aria-hidden="true"></i>
                }
                {{ notePriority }}
              </dd>
            </div>
            <div class="sm:col-span-2">
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Tags</dt>
              <dd class="m-0 mt-1 flex flex-wrap gap-2">
                @for (tag of noteTags; track tag._id ?? tag.name) {
                  <button
                    type="button"
                    class="note-tag-chip inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2.5 text-xs font-medium text-(--p-text-color) hover:bg-(--p-content-hover-background) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-slate-600 dark:bg-slate-800"
                    [title]="'Show notes tagged ' + tag.name"
                    (click)="filterByTag(tag)">
                    <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'" [title]="tag.color || 'No tag color'"></span>
                    {{ tag.name }}
                  </button>
                }
                @for (tag of privateNoteTags; track tag._id) {
                  <span
                    class="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-300 bg-white px-2.5 text-xs font-medium text-(--p-text-color) dark:border-slate-600 dark:bg-slate-800"
                    [title]="'Private tag — only you can see it'">
                    <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
                    <i class="pi pi-lock !text-[0.625rem] text-(--p-text-muted-color)" aria-hidden="true"></i>
                    <span class="sr-only">Private tag:</span>{{ tag.name }}
                  </span>
                }
                @if (!tagCount) {
                  <span class="text-sm text-(--p-text-muted-color)">No tags</span>
                }
              </dd>
            </div>
            <!-- Same fields as Edit's General section. -->
            <div class="sm:col-span-2">
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Keywords</dt>
              <dd class="m-0 mt-0.5 whitespace-pre-line break-words text-sm">{{ note.Keywords || '—' }}</dd>
            </div>
            @if (note.AiKeywords) {
              <div class="sm:col-span-2">
                <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI keywords</dt>
                <dd class="m-0 mt-0.5 whitespace-pre-line break-words text-sm">{{ note.AiKeywords }}</dd>
              </div>
            }
          </dl>
        </details>

        <details open class="group rounded-lg border border-surface-200 bg-surface-0 dark:border-surface-700 dark:bg-surface-900">
          <summary class="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-base font-semibold focus-visible:outline-2 focus-visible:outline-primary">
            Dates &amp; Timeline
            <span class="material-symbols-outlined transition-transform group-open:rotate-180" aria-hidden="true">expand_more</span>
          </summary>
          <div class="px-3 pb-3">
            <dl class="m-0 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Reference date</dt><dd class="m-0 mt-0.5 text-sm">{{ (note.ReferenceDate || note.created_on) | date: 'dd.MMM.yyyy' }}</dd></div>
              @if (isEventType || note.StartDate) {
                <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Start</dt><dd class="m-0 mt-0.5 text-sm">{{ note.StartDate ? (note.StartDate | date: 'dd.MMM.yyyy HH:mm') : '—' }}</dd></div>
              }
              @if (isEventType || note.EndDate) {
                <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">End</dt><dd class="m-0 mt-0.5 text-sm">{{ note.EndDate ? (note.EndDate | date: 'dd.MMM.yyyy HH:mm') : '—' }}</dd></div>
              }
              <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Due</dt><dd class="m-0 mt-0.5 text-sm" data-testid="view-due-date">{{ note.dueDate ? (note.dueDate | date: 'dd.MMM.yyyy HH:mm') : '—' }}</dd></div>
              <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Completed</dt><dd class="m-0 mt-0.5 text-sm" data-testid="view-completed-on">{{ note.completedOn ? (note.completedOn | date: 'dd.MMM.yyyy HH:mm') : '—' }}</dd></div>
            </dl>
            <dl class="m-0 mt-4 grid grid-cols-1 gap-x-6 gap-y-3 border-t border-surface-200 pt-3 text-(--p-text-muted-color) dark:border-surface-700 sm:grid-cols-2">
              <div><dt class="text-xs font-medium uppercase tracking-wide">Created</dt><dd class="m-0 mt-0.5 text-sm">{{ note.created_on | date: 'dd.MMM.yyyy HH:mm' }}</dd></div>
              <div><dt class="text-xs font-medium uppercase tracking-wide">Modified</dt><dd class="m-0 mt-0.5 text-sm">{{ note.last_modified | date: 'dd.MMM.yyyy HH:mm' }}</dd></div>
            </dl>
          </div>
        </details>

        <details open class="group rounded-lg border border-surface-200 bg-surface-0 dark:border-surface-700 dark:bg-surface-900">
          <summary class="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-base font-semibold focus-visible:outline-2 focus-visible:outline-primary">
            AI &amp; Insights
            <span class="material-symbols-outlined transition-transform group-open:rotate-180" aria-hidden="true">expand_more</span>
          </summary>
          <div class="space-y-4 px-3 pb-3 text-sm">
            @if (aiSummary; as summary) {
              @if (summary.summary_error) {
                <p class="m-0 rounded-md border border-red-300 bg-red-50 p-2 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200" role="alert">{{ summary.summary_error }}</p>
              }
              @if (summary.summary_stale) {
                <p class="m-0 text-amber-700 dark:text-amber-300"><i class="pi pi-exclamation-triangle mr-1" aria-hidden="true"></i>This summary may be out of date.</p>
              }
              @if (summary.summary_one_sentence) {
                <div><div class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Summary</div><p class="m-0 mt-0.5 whitespace-pre-line">{{ summary.summary_one_sentence }}</p></div>
              }
              @if (aiParagraphIfDistinct) {
                <div><div class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Details</div><p class="m-0 mt-0.5 whitespace-pre-line">{{ aiParagraphIfDistinct }}</p></div>
              }
              @if (summary.summary_bullets?.length) {
                <div>
                  <div class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Key points</div>
                  <ul class="m-0 mt-1 list-disc space-y-1 pl-5">
                    @for (bullet of summary.summary_bullets; track $index) { <li>{{ bullet }}</li> }
                  </ul>
                </div>
              }
              @if (summary.summary_keywords?.length) {
                <div>
                  <div class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Keywords</div>
                  <div class="mt-1 flex flex-wrap gap-2">
                    @for (kw of summary.summary_keywords; track $index) {
                      <span class="rounded-full bg-surface-100 px-3 py-1 text-xs dark:bg-surface-800">{{ kw }}</span>
                    }
                  </div>
                </div>
              }
            } @else {
              <p class="m-0 text-(--p-text-muted-color)">No AI summary is available for this note.</p>
            }

          </div>
        </details>

        <details open class="group rounded-lg border border-surface-200 bg-surface-0 dark:border-surface-700 dark:bg-surface-900">
          <summary class="flex min-h-11 cursor-pointer list-none items-center justify-between px-3 text-base font-semibold text-(--p-text-muted-color) focus-visible:outline-2 focus-visible:outline-primary">
            Technical details
            <span class="material-symbols-outlined transition-transform group-open:rotate-180" aria-hidden="true">expand_more</span>
          </summary>
          <dl class="m-0 grid grid-cols-1 gap-x-6 gap-y-3 px-3 pb-3 text-(--p-text-muted-color) sm:grid-cols-2">
            <div class="sm:col-span-2">
              <dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Note ID</dt>
              <dd class="m-0 mt-0.5 flex items-center gap-1 text-sm">
                <code class="min-w-0 break-all">{{ note._id }}</code>
                <button type="button" class="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent hover:bg-(--p-content-hover-background)" aria-label="Copy note ID" title="Copy note ID" (click)="copyNoteId()">
                  <span class="material-symbols-outlined !text-lg" aria-hidden="true">content_copy</span>
                </button>
              </dd>
            </div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Note size</dt><dd class="m-0 mt-0.5 text-sm">{{ noteSizeLabel }}</dd></div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Space</dt><dd class="m-0 mt-0.5 text-sm">{{ spaceName }}</dd></div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Space ID</dt><dd class="m-0 mt-0.5 break-all text-sm">{{ note.spaceId || 'Private' }}</dd></div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Sync status</dt><dd class="m-0 mt-0.5 text-sm">{{ note.syncStatus || '—' }}</dd></div>
            <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">Last synced</dt><dd class="m-0 mt-0.5 text-sm">{{ note.syncTime ? (note.syncTime | date: 'dd.MMM.yyyy HH:mm') : '—' }}</dd></div>
            @if (aiSummary; as ai) {
              <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI summary status</dt>
                <dd class="m-0 mt-0.5 flex items-center gap-1 text-sm">
                  <span class="material-symbols-outlined !text-base" [ngClass]="aiSummaryStatusIconClass(ai.summary_status)" aria-hidden="true">{{ aiSummaryStatusIcon(ai.summary_status) }}</span>
                  {{ aiSummaryStatusLabel(ai.summary_status) }}
                </dd></div>
              @if (ai.summary_model) { <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI model</dt><dd class="m-0 mt-0.5 text-sm">{{ ai.summary_model }}</dd></div> }
              @if (ai.summary_note_revision) { <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI revision</dt><dd class="m-0 mt-0.5 break-all text-sm">{{ ai.summary_note_revision }}</dd></div> }
              @if (ai.summary_requested_at) { <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI requested</dt><dd class="m-0 mt-0.5 text-sm">{{ ai.summary_requested_at | date: 'dd.MMM.yyyy HH:mm' }}</dd></div> }
              @if (ai.summary_generated_at) { <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI generated</dt><dd class="m-0 mt-0.5 text-sm">{{ ai.summary_generated_at | date: 'dd.MMM.yyyy HH:mm' }}</dd></div> }
              @if (ai.summary_token_input || ai.summary_token_output) {
                <div><dt class="text-xs font-medium uppercase tracking-wide text-(--p-text-muted-color)">AI tokens</dt><dd class="m-0 mt-0.5 text-sm">{{ ai.summary_token_input ?? '—' }} in / {{ ai.summary_token_output ?? '—' }} out</dd></div>
              }
            }
          </dl>
        </details>
      </div>
    </ng-template>

    <!-- ===================== ATTACHMENTS ===================== -->
    <ng-template notePanel="attachments">
      <div class="w-full px-2 py-2 sm:px-3 sm:py-3 lg:px-4">
        <!-- Always rendered (not gated on attachmentCount, which is only a same-tick estimate from
             note.Attachments): FileUploadComponent does its own robust lookup — backend endpoint,
             then IndexedDB — which can find attachments even when note.Attachments is empty/stale,
             and its own empty-state message covers the true zero case. Gating this on
             attachmentCount previously meant a note whose Attachments array didn't include an
             attachment that nonetheless existed (backend-side) never even tried to look it up. -->
        <app-file-upload [noteId]="note._id" [spaceId]="note.spaceId" [noteAttachments]="note.Attachments" [readOnly]="true" (countChange)="resolvedAttachmentCount = $event"></app-file-upload>
      </div>
    </ng-template>

    <!-- ===================== RELATED ===================== -->
    <ng-template notePanel="related">
      <div class="grid w-full grid-cols-1 items-start gap-8 px-2 py-2 sm:px-3 sm:py-3 lg:px-4 xl:grid-cols-2">

        <section aria-labelledby="ws-linked-heading">
          <h2 id="ws-linked-heading" class="m-0 mb-2 flex min-h-11 items-center gap-1 text-base font-semibold">Linked notes <span class="font-normal text-(--p-text-muted-color)">({{ relatedNotes.length }})</span></h2>
          @if (relatedNoteObjects.length) {
            <ul class="m-0 list-none divide-y divide-surface-200 rounded-lg border border-surface-200 bg-surface-0 p-0 dark:divide-surface-700 dark:border-surface-700 dark:bg-surface-900">
              @for (ref of relatedNoteObjects; track ref.id) {
                <li class="flex min-h-14 items-center gap-3 px-3 py-2" [ngStyle]="relatedNoteRowStyle(ref.status)">
                  <div class="min-w-0 flex-1">
                    <a class="block truncate font-medium hover:underline focus-visible:outline-2 focus-visible:outline-primary" [routerLink]="['/view', ref.id]" target="_blank" rel="noopener" [ngStyle]="relatedNoteRowStyle(ref.status)">
                      {{ ref.title || '(Untitled)' }}
                    </a>
                    <div class="text-xs opacity-80">
                      {{ ref.status || '—' }}
                      @if (ref.lastModified) { · {{ ref.lastModified | date: 'dd MMM yyyy' }} }
                    </div>
                  </div>
                  @if (ref.aiSummary?.summary_status; as aiStatus) {
                    <span
                      class="material-symbols-outlined shrink-0 !text-xl"
                      [ngClass]="aiSummaryStatusIconClass(aiStatus)"
                      [attr.aria-label]="'AI summary: ' + aiSummaryStatusLabel(aiStatus)"
                      [pTooltip]="getAiSummaryTooltip(ref.aiSummary)"
                      tooltipPosition="left">{{ aiSummaryStatusIcon(aiStatus) }}</span>
                  }
                </li>
              }
            </ul>
          } @else {
            <app-note-empty-state icon="link_off" title="No linked notes" description="Notes you link to this one will appear here." />
          }
        </section>

        <section aria-labelledby="ws-similar-heading">
          <div class="mb-2 flex items-center justify-between gap-3">
            <h2 id="ws-similar-heading" class="m-0 text-base font-semibold">Similar notes <span class="text-xs font-normal text-(--p-text-muted-color)">(AI)</span></h2>
            <p-button
              label="Find similar"
              icon="pi pi-sparkles"
              size="small"
              severity="secondary"
              styleClass="!h-11"
              [loading]="similarNotesLoading"
              [disabled]="similarNotesLoading || !note._id || !isOnline"
              [title]="!isOnline ? 'AI features are only available in online mode' : 'Find notes similar to this one'"
              (onClick)="findSimilarNotes()">
            </p-button>
          </div>

          @if (similarNotesError) {
            <p class="m-0 text-sm text-red-600 dark:text-red-300" role="alert">{{ similarNotesError }}</p>
          }

          @if (similarNotesLoading) {
            <p class="m-0 text-sm text-(--p-text-muted-color)" role="status">Searching…</p>
          } @else if (similarNotesResults.length) {
            <ul class="m-0 list-none divide-y divide-surface-200 rounded-lg border border-surface-200 bg-surface-0 p-0 dark:divide-surface-700 dark:border-surface-700 dark:bg-surface-900">
              @for (item of similarNotesResults; track getSimilarNoteId(item)) {
                <li class="flex min-h-14 items-center gap-3 px-3 py-2" [ngStyle]="relatedNoteRowStyle(item.Status || '')">
                  <div class="min-w-0 flex-1">
                    <a class="block truncate font-medium hover:underline focus-visible:outline-2 focus-visible:outline-primary" [routerLink]="['/view', getSimilarNoteId(item)]" target="_blank" rel="noopener" [ngStyle]="relatedNoteRowStyle(item.Status || '')">
                      {{ similarNoteTitle(item) }}
                    </a>
                    <div class="text-xs opacity-80">
                      {{ item.Status || '—' }} · {{ formatSimilarNoteDate(item) }}
                    </div>
                  </div>
                  @if (item.final_score != null) {
                    <span class="shrink-0 text-xs tabular-nums opacity-80" title="Similarity score">{{ item.final_score | number: '1.2-2' }}</span>
                  }
                </li>
              }
            </ul>
          } @else if (similarRequested && !similarNotesError) {
            <app-note-empty-state icon="search_off" title="No similar notes found" />
          } @else if (!similarNotesError) {
            <p class="m-0 text-sm text-(--p-text-muted-color)">
              {{ isOnline ? 'Find notes with similar content. This uses AI and needs a connection.' : 'Similar-note search needs a connection.' }}
            </p>
          }
        </section>
      </div>
    </ng-template>

    <!-- ===================== COMMENTS ===================== -->
    <ng-template notePanel="comments">
      <div class="w-full px-2 py-2 sm:px-3 sm:py-3 lg:px-4">
        @if (hasComments) {
          <div [ngClass]="editorSurfaceClasses">
          @if (!showRawMarkup) {
            <app-rich-editor
              editorId="EditComments"
              [noteId]="note._id"
              [spaceId]="note.spaceId"
              [content]="renderMarkup(note.CommentsHtml)"
              [readOnly]="true"
              [placeholderText]="''">
            </app-rich-editor>
          } @else {
            <pre class="m-0 overflow-x-auto whitespace-pre-wrap break-words text-sm">{{ note.CommentsHtml || '' }}</pre>
          }
          </div>
        } @else {
          <app-note-empty-state
            icon="chat_bubble_outline"
            title="No comments"
            description="Comments on this note will appear here. Threaded, multi-user comments are planned." />
        }
      </div>
    </ng-template>

    <!-- ===================== HISTORY ===================== -->
    <ng-template notePanel="history">
      <div class="w-full px-2 py-2 sm:px-3 sm:py-3 lg:px-4">
        <app-note-empty-state
          icon="history"
          title="Version history will be available here"
          description="Versions, change timestamps, authors, comparison and restore are planned." />
      </div>
    </ng-template>
  </app-note-shell>
} @else if (isLoading()) {
  <!-- Intentional loading state instead of flashing partial content. -->
  <div class="flex h-full flex-col bg-surface-50 dark:bg-surface-950" role="status" aria-live="polite" aria-busy="true">
    <div class="h-14 border-b border-surface-200 bg-surface-0 dark:border-surface-700 dark:bg-surface-900"></div>
    <div class="w-full animate-pulse space-y-3 px-2 py-3 lg:px-4">
      <div class="h-4 w-1/3 rounded bg-surface-200 dark:bg-surface-700"></div>
      <div class="h-4 w-full rounded bg-surface-200 dark:bg-surface-700"></div>
      <div class="h-4 w-5/6 rounded bg-surface-200 dark:bg-surface-700"></div>
      <div class="h-4 w-2/3 rounded bg-surface-200 dark:bg-surface-700"></div>
      <span class="sr-only">Loading note…</span>
    </div>
  </div>
} @else {
  <div class="flex h-full flex-col">
    <app-note-empty-state icon="error_outline" title="Note not found" description="This note could not be loaded.">
      <p-button label="Back to notes" icon="pi pi-arrow-left" (onClick)="pageMenu('Home')"></p-button>
    </app-note-empty-state>
  </div>
}

<!-- Image zoom: a click/tap on an image in the note shows it as large as fits the window over the dimmed
     page, where it can be zoomed (pinch, wheel, +/−) and moved; a tap/click (or Escape) returns to the note. -->
@if (zoomedImage) {
  <app-image-lightbox [image]="zoomedImage" (closed)="closeImageZoom()"></app-image-lightbox>
}
